import { Subsystem } from "@dto/architecture.dto";
import { ConfigurationChange, ConfigurationChangesDTO } from "@dto/configurationChanges.dto";
import { ConfigurationChangesBox } from "@dto/pageResponse.dto";
import { SettingKey } from "@dto/settings.dto";
import { Card, InputNumber, Tabs } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { XmButton } from "src/component/button/XmButton";
import XmTabsSticky from "src/component/stickyTab/XmTabsSticky";
import { Log } from "src/service/log.service";
import { getSettings, saveSettings } from "src/service/settings.service";
import { getApi } from "src/util/apiCalls";
import { GLOB } from 'src/util/Glob';
import { getAllSubsystems } from "../alerting/alertingService";
import CfgFilter from "./CfgFilter/CfgFilters";
import useCfgFilterStore from "./CfgFilterStore";
import './CfgTracker.less';
import { CFGTrackerTable } from "./CFGTrackerTable";

interface ISubsystem extends Subsystem {
  hw_type_label?: string;
}

export interface Tab {
  label: string;
  key: string;
  items: ConfigurationChange[];
}

export interface ComponentProps {
  items: ConfigurationChange[];
  range: [Dayjs, Dayjs],
  onRangeChange: (range?: { start: Dayjs, end: Dayjs; }) => void;
  isLoading: boolean;
  showTechnologyColumn?: boolean;
}

// Can be same ref for all instances of cfgTrackerWrapper
let hwTypeLabels: ISubsystem[] = [];

// AxiosRequestConfig<unknown>['params'] <--- useless for params ANY
type ApiParams = ({
  filter_id?: string;
  end: number,
  start: number,
} | {
  filter_id?: string;
  start: number;
} | {
  filter_id?: string;
});

type Props = Partial<{
  box: ConfigurationChangesBox; // CFG Tracker Tab prop
  params: ApiParams; // Filter Tab prop
  isNew: boolean; // Filter Tab prop
  showTechnologyColumn?: boolean; // Filter Tab prop
}>;

export default function cfgTrackerWrapper(Component: React.ComponentType<ComponentProps>) {
  function Wrapper({ box, params, isNew, ...rest }: Props) {
    const [items, setItems] = useState<ConfigurationChange[]>();
    const [isLoading, setIsLoading] = useState(true);
    const [range, setDateRange] = useState<[Dayjs, Dayjs]>([
      dayjs().subtract(7, 'days'),
      dayjs()
    ]);

    const formSaved = useCfgFilterStore(s => s.formSaved);

    const loadData = useCallback(async (signal?: AbortSignal) => {
      if (isNew) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        if (!hwTypeLabels.length) {
          const { data: { data: tmpHwTypeLabels } } = await getAllSubsystems();
          hwTypeLabels = tmpHwTypeLabels;
          if (!hwTypeLabels.length) {
            setIsLoading(false);
            return;
          }
        }

        const startInSec = Math.floor(range[0].valueOf() / 1000);
        const endInSec = Math.floor(range[1].valueOf() / 1000);
        const apiParams = params ? {
          start: startInSec,
          end: endInSec,
          ...params
        } : {
          start: startInSec,
          end: endInSec,
          hw_type: box?.hw_type,
          subsystem: box?.subsystem,
          item_ids: box?.item_ids?.join(',')
        };

        const { data: { data } } = await getApi<ConfigurationChangesDTO>(
          '/api/configuration/v1/tracker/events',
          signal,
          { params: apiParams }
        );

        const dataWithLabels = data.map((obj): ConfigurationChange => ({
          ...obj,
          hw_type: hwTypeLabels.find((el) => el.hw_type === obj.hw_type)?.hw_type_label,
          subsystem: hwTypeLabels.find((el) => el.subsystem === obj.subsystem && el.hw_type === obj.hw_type)?.label,
        }));

        setItems(dataWithLabels);
      } catch (error) {
        Log.error('Failed to fetch configuration changes', error);
      } finally {
        setIsLoading(false);
      }
    }, [isNew, range, params, box, setItems, setIsLoading]);

    useEffect(() => {
      if (formSaved) {
        void loadData();
      }
    }, [formSaved, loadData]);

    useEffect(() => {
      const controller = new AbortController();
      void loadData(controller.signal);

      return () => controller.abort();
    }, [loadData]);

    const handleRangeChange = (range?: { start: Dayjs, end: Dayjs; }) => {
      if (range) {
        setDateRange([range.start, range.end]);
        return;
      }

      setDateRange([dayjs().subtract(7, 'days'), dayjs()]);
    };

    return <Component {...rest} items={items} onRangeChange={handleRangeChange} isLoading={isLoading} range={range} />;
  };

  Wrapper.displayName = 'CfgTrackerWrapper';
  return Wrapper;
}

export const CFGTrackerWithTabs: FC<ComponentProps> = ({ items, isLoading, onRangeChange, range }) => {
  const [prevItems, setPrevItems] = useState<ConfigurationChange[]>();
  const menuData = useRef<Tab[]>([]);

  if (items !== prevItems && !isLoading) {
    setPrevItems(items);
    menuData.current = [
      { key: 'all', label: 'All', items },
      ...[...new Set(items.map(obj => obj.hw_type))].map(
        val => ({
          key: val,
          label: val,
          items: items.filter(item => item.hw_type == val)
        })
      )
    ];
  }

  return (
    <XmTabsSticky
      items={[{
        key: 'default',
        label: 'Change Tracker',
        className: 'xm-cfg-tracker xm-cfg-tracker-top-menu',
        children:
          <Tabs
            defaultActiveKey='all'
            className='xm-cfg-tracker-left-menu'
            tabPlacement="start"
            items={menuData.current.map(item => ({
              ...item,
              children:
                <CFGTrackerTable
                  items={item.items}
                  isLoading={isLoading}
                  showTechnologyColumn={item.key == 'all'}
                  onRangeChange={onRangeChange}
                  range={range}
                />
            }))}
          />
      },
      {
        key: "filters",
        label: "Filters",
        className: "xm-cfg-tracker-top-menu",
        children: <CfgFilter />
      },
      {
        key: "options",
        label: "Options",
        className: "xm-cfg-tracker-top-menu",
        children: <SetRetentionModal />
      }
      ]}
    />
  );
};

function SetRetentionModal() {
  const [retention, setRetention] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const keys = SettingKey.GLOB;
    getSettings(keys.PREFIX, controller).then(response => {
      const initRetention = response.data.data.GLOB_CONF_CHANGES_RETENTION;
      setRetention(+initRetention);
    }).catch(
      error => Log.error("Failed to obtain Retention value!", error)
    ).finally(() => setIsLoading(false));

    return () => controller.abort();
  }, []);

  const handleChange = (newRetention: number) => setRetention(newRetention);
  const handleOk = () => {
    setIsLoading(true);
    void saveSettings({ 'GLOB_CONF_CHANGES_RETENTION': retention.toString() }).then(
      () => {
        Log.info('Settings Retention value updated');
      },
      error => Log.error('Failed to save Retention value!', error)
    ).finally(() =>
      setIsLoading(false)
    );
  };

  const { readonly, isAdmin } = GLOB.userInfo;

  return (
    <Card title="Retention">
      <span>Set retention: </span>
      <InputNumber placeholder='' min={1} disabled={!isAdmin || readonly || isLoading} value={retention} onChange={handleChange} />
      <span> days</span>
      <XmButton type="primary" className="xm-btn-retention" onClick={handleOk} disabled={!isAdmin || isLoading}>Save</XmButton>
    </Card>
  );
}